import React, { useState, useEffect } from 'react';
import { R2_ASSETS } from '../utils/assetConstants';

/**
 * WebinarImage — loading skeleton + graceful multi-tier fallback.
 *
 * Fallback chain:
 *   1. event.image_url  (external URL from RSS or admin)
 *   2. R2 branded fallback  (assets.testingvala.com/fallbacks/webinar-fallback.webp)
 *   3. Inline SVG placeholder  (zero external dependency — always renders)
 *
 * The inline SVG is the final safety net so "Asset Offline" is never shown.
 * R2 images are NOT mirrored — external URLs are used directly.
 */
const WebinarImage = ({ src, alt, className = "", isFeatured = false }) => {
  const [imgSrc,     setImgSrc]     = useState(src || R2_ASSETS.BRANDED_FALLBACK);
  const [loading,    setLoading]    = useState(true);
  const [usingSvg,   setUsingSvg]   = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    setImgSrc(src || R2_ASSETS.BRANDED_FALLBACK);
    setLoading(true);
    setUsingSvg(false);
    setRetryCount(0);
  }, [src]);

  const handleLoad = () => setLoading(false);

  const handleError = () => {
    if (retryCount < 1) {
      // First failure: try R2 branded fallback
      setRetryCount(1);
      setImgSrc(R2_ASSETS.BRANDED_FALLBACK);
    } else {
      // R2 also failed (404 or offline) — render inline SVG
      // Zero external dependency — always works regardless of R2 status
      setLoading(false);
      setUsingSvg(true);
    }
  };

  return (
    <div className={`relative overflow-hidden bg-[#0D1117] ${className}`}>

      {/* Loading skeleton */}
      {loading && !usingSvg && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#161B22] animate-pulse">
          <svg className="w-8 h-8 text-[#30363D]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}

      {/* Inline SVG placeholder — fires when both external URL and R2 fail */}
      {usingSvg && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-[#161B22] to-[#0D1117]">
          <svg viewBox="0 0 400 225" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
            <rect width="400" height="225" fill="#161B22"/>
            {/* Grid lines */}
            <line x1="0" y1="75" x2="400" y2="75" stroke="#21262D" strokeWidth="1"/>
            <line x1="0" y1="150" x2="400" y2="150" stroke="#21262D" strokeWidth="1"/>
            <line x1="133" y1="0" x2="133" y2="225" stroke="#21262D" strokeWidth="1"/>
            <line x1="266" y1="0" x2="266" y2="225" stroke="#21262D" strokeWidth="1"/>
            {/* Center icon */}
            <circle cx="200" cy="100" r="28" fill="#21262D" stroke="#30363D" strokeWidth="1.5"/>
            <path d="M190 93 L210 93 L210 107 L190 107 Z" fill="none" stroke="#484F58" strokeWidth="1.5" strokeLinejoin="round"/>
            <circle cx="194" cy="97" r="2" fill="#484F58"/>
            <path d="M190 107 L196 101 L201 106 L205 102 L210 107" fill="none" stroke="#484F58" strokeWidth="1.5" strokeLinejoin="round"/>
            {/* Label */}
            <text x="200" y="145" textAnchor="middle" fill="#484F58" fontSize="9" fontFamily="monospace" letterSpacing="2">
              {alt ? alt.substring(0, 30) : 'WEBINAR'}
            </text>
            {/* TestingVala brand mark */}
            <text x="200" y="200" textAnchor="middle" fill="#30363D" fontSize="8" fontFamily="monospace" letterSpacing="1">
              TESTINGVALA
            </text>
          </svg>
        </div>
      )}

      {/* Main image — hidden while loading, shown on load */}
      {!usingSvg && (
        <img
          src={imgSrc}
          alt={alt || 'Webinar'}
          onLoad={handleLoad}
          onError={handleError}
          className={`w-full h-full object-cover transition-opacity duration-500 ${
            loading ? 'opacity-0' : 'opacity-100'
          }`}
        />
      )}

    </div>
  );
};

export default WebinarImage;
